import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { setUserOnline, setUserOffline } from '../utils/onlineStatus';
import { apiPost, apiGet, suppressAuthErrorLogging, restoreAuthErrorLogging } from '../utils/api';

// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: { location: window?.location?.pathname || '/', ...context },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {}
};

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      loading: false,
      error: null,
      isAdmin: false,
      hydrated: false,
      authInitialized: false, // New flag to track Firebase auth initialization

      // Initialize authentication state
      initializeAuth: async () => {
        set({ loading: true, error: null });
        
        return new Promise((resolve) => {
          const auth = getAuth();
          
          // Listen for Firebase auth state changes
          const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            try {
              if (firebaseUser) {
                // User is signed in with Firebase, now check backend session
                const res = await apiGet('/api/auth/me');
                const data = await res.json();
                
                if (res.ok && data && data.email) {
                  const user = data;
                  // Ensure user.uid is set to firebaseUid for presence tracking
                  if (!user.uid) {
                    user.uid = user.firebaseUid || user.id;
                  }
                  const isAdmin = !!(user.isAdmin || user.role === 'admin');
                  set({ 
                    user, 
                    isAdmin, 
                    loading: false, 
                    hydrated: true,
                    authInitialized: true,
                    error: null 
                  });
                  setUserOnline(firebaseUser);
                } else {
                  // Firebase user exists but no valid backend session
                  set({ 
                    user: null, 
                    isAdmin: false, 
                    loading: false, 
                    hydrated: true,
                    authInitialized: true 
                  });
                  setUserOffline(firebaseUser);
                }
              } else {
                // No Firebase user – still check backend session via cookie
                try {
                  const res = await apiGet('/api/auth/me');
                  const data = await res.json();
                  if (res.ok && data && data.email) {
                    const user = data;
                    const isAdmin = !!(user.isAdmin || user.role === 'admin');
                    set({ user, isAdmin, loading: false, hydrated: true, authInitialized: true, error: null });
                  } else {
                    set({ user: null, isAdmin: false, loading: false, hydrated: true, authInitialized: true });
                  }
                } catch (e) {
                  set({ user: null, isAdmin: false, loading: false, hydrated: true, authInitialized: true });
                }
              }
            } catch (error) {
              // Handle any unexpected errors gracefully
              set({ 
                user: null, 
                isAdmin: false, 
                loading: false, 
                hydrated: true,
                authInitialized: true 
              });
            }
            resolve();
          });
          
          // Cleanup function
          return () => unsubscribe();
        });
      },

      login: async (email, password) => {
        set({ loading: true, error: null });
        try {
          // First try backend login to check if password was changed
          const backendRes = await apiPost('/api/auth/login', { email, password });
          
          if (!backendRes.ok) {
            const backendData = await backendRes.json();
            throw new Error(backendData.message || 'Login failed');
          }

          const backendData = await backendRes.json();
          const user = backendData.user;
          const isAdmin = !!(user.isAdmin || user.role === 'admin');

          // If backend login succeeds, try to sync with Firebase
          try {
            const auth = getAuth();
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            setUserOnline(userCredential.user);
          } catch (firebaseError) {
            // If Firebase login fails (e.g., password was changed), 
            // we still have a valid backend session, so continue
            safeReportError('firebase_sync_failed_backend_login_ok');
            
            // Try to re-authenticate Firebase with the new password
            // This handles cases where password was changed via reset
            try {
              const auth = getAuth();
              if (auth.currentUser) {
                // Sign out and try to sign in again
                await signOut(auth);
                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                setUserOnline(userCredential.user);
              }
            } catch (retryError) {
              safeReportError('firebase_reauthentication_failed');
              // Continue with backend session only
            }
          }

          // Set user with backend data
          set({ 
            user, 
            loading: false, 
            isAdmin,
            error: null 
          });
          
          return user;
        } catch (error) {
          set({ error: 'Login failed. Please check your credentials.', loading: false });
          throw error;
        }
      },

      signup: async (email, password) => {
        set({ loading: true, error: null });
        try {
          const auth = getAuth();
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          
          // Get backend user data
          const backendRes = await apiPost('/api/auth/register', { email, password });
          
          const backendData = await backendRes.json();
          if (!backendRes.ok) {
            throw new Error(backendData.message || 'Registration failed');
          }

          // Set user with backend data
          const user = backendData.user;
          const isAdmin = !!(user.isAdmin || user.role === 'admin');
          set({ 
            user, 
            loading: false, 
            isAdmin,
            error: null 
          });
          
          setUserOnline(userCredential.user);
          return user;
        } catch (error) {
          set({ error: 'Registration failed. Please try again.', loading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          // Enable error suppression for auth endpoints
          suppressAuthErrorLogging();
          
          // First, clear the local state immediately
          set({ user: null, loading: false, isAdmin: false, hydrated: true });
          
          // Then clear backend session and Firebase auth
          await apiPost('/api/auth/logout');
          const auth = getAuth();
          const user = auth.currentUser;
          setUserOffline(user);
          await signOut(auth);
        } catch (error) {
          // Even if logout fails, ensure local state is cleared
          set({ user: null, loading: false, isAdmin: false, hydrated: true });
          // Logout error (non-critical)
        } finally {
          // Restore error logging after a delay
          setTimeout(() => {
            restoreAuthErrorLogging();
          }, 2000);
        }
      },

      setUser: (user) => {
        if (user) {
          // Use firebaseUid for presence tracking, fallback to id if needed
          if (!user.uid) {
            user.uid = user.firebaseUid || user.id;
          }
          const isAdmin = !!(user.isAdmin || user.role === 'admin');
          set({ user, isAdmin, hydrated: true });
        } else {
          set({ user: null, isAdmin: false, hydrated: true });
        }
      },

      // Sync Firebase password with backend when password is changed
      syncPassword: async (email, newPassword) => {
        try {
          const auth = getAuth();
          
          // If there's a current Firebase user, sign them out
          if (auth.currentUser) {
            await signOut(auth);
          }
          
          // Try to sign in with new password to sync Firebase
          const userCredential = await signInWithEmailAndPassword(auth, email, newPassword);
          setUserOnline(userCredential.user);
          
          return true;
        } catch (error) {
          safeReportError('password_sync_failed');
          return false;
        }
      },
      
      checkSession: async () => {
        // Always check server session (cookie) regardless of Firebase state
        set({ loading: true });
        try {
          const res = await apiGet('/api/auth/me');
          const data = await res.json();
          if (res.ok && data && data.email) {
            const user = data;
            // Ensure user.uid is set to firebaseUid for presence tracking
            if (!user.uid) {
              user.uid = user.firebaseUid || user.id;
            }
            const isAdmin = !!(user.isAdmin || user.role === 'admin');
            set({ user, isAdmin, loading: false });
          } else {
            set({ user: null, isAdmin: false, loading: false });
          }
        } catch (error) {
          set({ user: null, isAdmin: false, loading: false });
        }
        set({ hydrated: true });
      },

      // Getter for checking if auth is still loading
      getAuthLoading: () => {
        const state = get();
        return !state.hydrated || !state.authInitialized || state.loading;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user,
        isAdmin: state.isAdmin
      }),
    }
  )
);

export default useAuthStore; 